import React, { Component, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import Alert from 'components/ui/Alert';
import { isEqual, omitBy, pick, isNil } from 'lodash';
import { DropdownButton, MenuItem, OverlayTrigger, Tooltip, } from 'react-bootstrap';
import classnames from 'classnames';
import AccessorialRequestActionCreators from 'actions/AccessorialRequestActionCreators';
import ShipmentsActionCreators from 'actions/ShipmentsActionCreators';
import _ from 'underscore';
import ExceptionStore from 'stores/ExceptionStore';
import { humanize, titleize } from 'underscore.string';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import { validDecimal } from 'utils/accounting-util';
import formatMoney from 'utils/formatters/Money';
import { accessorialCanBeInvoiced, accessorialPaid, accessorialProcessed, userCanApproveAccessorials as userCanApproveAccessorialsHelper, checkIfEquipmentIsMissingForAccessorial, checkWhichSideAccessorialAutoDeclined, isCarrierMissingInAccessorial, } from 'helpers/accessorials';
import ErrorLabel from 'components/v2/forms/ErrorLabel';
import Roles from 'constants/UserRoles';
import { Translation, useTranslation, withTranslation } from 'react-i18next';
import { t } from 'i18next';
import USER_TYPES from 'constants/UserTypes';
import { getAccessorials } from 'utils/AccessorialWebAPIUtils';
import AuditComments from 'components/modules/comments/AuditComments';
import CargomaticLabel from 'components/v2/forms/Label';
import CargomaticInput from 'components/v2/input';
import CargomaticSelect from 'components/v2/select';
import Badge from 'components/v2/Badge';
import DateIconButton from 'components/v2/buttons/DateIconButton';
import ApproveButton from 'components/v2/buttons/ApproveButton';
import DeclineButton from 'components/v2/buttons/DeclineButton';
import EditButton from 'components/v2/buttons/EditButton';
import StatHeading from 'components/v2/StatHeading';
import { LOCATION_TYPES } from 'constants/location';
import { getDeclinedAccessorialsReasonCodes } from 'utils/ReasonCodesAPI';
import { getDocumentsV2 } from 'utils/DocumentsAPIUtils';
import { showToast } from 'stores/ToastStore';
import SessionStore from 'stores/SessionStore';
import ViewAccessorialRequestDocumentRequirements from 'components/modules/shipments/accessorials/ViewAccessorialDocumentRequirements';
import { isShipmentCanceled } from 'helpers/shipments';
import { isVendorChargePaid } from 'common/helpers/accessorials';
import { getAllEquipmentsByEquipId } from 'utils/EquipmentWebAPIUtils';
const AUTO_APPLY_NEEDS_REVIEW_INFO_TEXT_MAPPING = {
    true: 'AUTO_APPLY_NEEDS_REVIEW_INFO_TRUE',
    false: 'AUTO_APPLY_NEEDS_REVIEW_INFO_FALSE',
};
const DATE_FORMAT = 'MMM Do YYYY hh:mm A';
const locationTypes = LOCATION_TYPES.map((l) => {
    return {
        name: titleize(l),
        localizationSlug: l.toUpperCase(),
        value: l.toLowerCase(),
    };
});
function getMomentDate(date) {
    if (!date) {
        return null;
    }
    const momentDate = moment(date);
    if (momentDate.isValid()) {
        return momentDate;
    }
    return null;
}
function getUnitLabel(units, unitName) {
    if (unitName === 'hourly') {
        return t('COUNT_HOUR', { count: units, hourCount: units });
    }
    if (unitName === 'daily') {
        return t('COUNT_DAY', { count: units, dayCount: units });
    }
    return units;
}
function getLabelInfo(referenceAccessorial) {
    let label = 'AMOUNT';
    let info = 'THE_AMOUNT';
    if (referenceAccessorial) {
        const { rate_type: rateType } = referenceAccessorial;
        if (rateType === 'daily') {
            label = 'DAYS_LABEL';
            info = 'NUMBER_OF_DAYS';
        }
        if (rateType === 'hourly') {
            label = 'HOURS_LABEL';
            info = 'NUMBER_OF_HOURS';
        }
    }
    return [label, info];
}
function getRequestFields(accessorial, isCarrier = true) {
    const rateField = isCarrier ? 'carrier_rate' : 'shipper_rate';
    const unitsField = isCarrier ? 'carrier_unit_qty' : 'shipper_unit_qty';
    const beginDateField = isCarrier
        ? 'begin_datetime'
        : 'shipper_begin_datetime';
    const endDateField = isCarrier ? 'end_datetime' : 'shipper_end_datetime';
    const locationField = isCarrier ? 'location' : 'shipper_location';
    const descriptionField = isCarrier ? 'description' : 'shipper_description';
    const notesField = isCarrier ? 'carrier_note' : 'shipper_note';
    const beginDate = accessorial[beginDateField];
    const endDate = accessorial[endDateField];
    const location = accessorial[locationField];
    const description = accessorial[descriptionField];
    const units = accessorial[unitsField];
    const rate = accessorial[rateField];
    const notes = accessorial[notesField];
    return {
        rateField,
        unitsField,
        beginDateField,
        endDateField,
        locationField,
        descriptionField,
        notesField,
        rate,
        units,
        beginDate,
        endDate,
        location,
        description,
        notes,
    };
}
const defaultApproveMessage = 'CONFIRM_APPROVAL_MESSAGE';
const missingDocsApproveMessage = 'CONFIRM_APPROVE_MESSAGE_MISSING_DOCS';
// const declineMessage = 'Are you sure you want to decline?';
const approveTitle = 'CONFIRM_APPROVAL_TITLE';
const declineTitle = 'CONFIRM_DECLINE_TITLE';
const approveAllMessage = 'CONFIRM_APPROVAL_ALL_MESSAGE';
// const declineAllMessage = 'Are you sure you want to decline all?';
const colorLabelMap = {
    approved: 'green',
    accepted: 'blue',
    declined: 'red',
    'pending-admin': 'yellow',
    'pending-shipper': 'yellow',
};
const usingValidDecimals = (cost) => {
    if (!cost)
        return true;
    const charArray = cost.toString().split('');
    if (charArray.indexOf('.') > -1) {
        const index = charArray.indexOf('.');
        if (charArray[index + 3]) {
            return false;
        }
        return true;
    }
    return true;
};
const whitelist = [
    'carrier_status',
    'shipper_status',
    'shipper_rate',
    'carrier_rate',
    'shipper_unit_qty',
    'carrier_unit_qty',
    'admin_note',
    'shipper_note',
    'carrier_note',
    'unit_name',
    'equipment',
    'equipment_id',
    'message',
    'history',
    '_id',
    'location',
    'begin_datetime',
    'end_datetime',
    'vendor',
    'vendor_name',
    'description',
    'shipper_location',
    'shipper_begin_datetime',
    'shipper_end_datetime',
    'shipper_description',
    'carrier_decline_reason_code',
    'rate_source',
];
// Fields that can be edited and should be tracked for changes
const editableFieldsWhitelist = [
    'shipper_rate',
    'shipper_unit_qty',
    'shipper_note',
    'carrier_rate',
    'carrier_unit_qty',
    'carrier_note',
    'admin_note',
    'location',
    'begin_datetime',
    'end_datetime',
    'description',
    'shipper_location',
    'shipper_begin_datetime',
    'shipper_end_datetime',
    'shipper_description',
];
const TOOLTIPS = {
    PROCESSED: (React.createElement(Tooltip, { style: { zIndex: 99999 }, id: "tooltip_disabled_select" },
        React.createElement(Translation, null, (t) => t('ACCESSORIALS_TOOLTIP_PROCESSED')))),
    UNITS: (React.createElement(Tooltip, { id: "units", style: { zIndex: 99999 } },
        React.createElement(Translation, null, (t) => t('ACCESSORIALS_TOOLTIP_UNITS')))),
    RATE: (React.createElement(Tooltip, { id: "rate", style: { zIndex: 99999 } },
        React.createElement(Translation, null, (t) => t('ACCESSORIALS_TOOLTIP_RATE')))),
    SHIPPER_NOTES: (React.createElement(Tooltip, { id: "note", style: { zIndex: 99999 } },
        React.createElement(Translation, null, (t) => t('ACCESSORIALS_TOOLTIP_SHIPPER_NOTES')))),
    MISSING_DOCS: (React.createElement(Tooltip, { id: "missing_accessorial_docs", style: { zIndex: 99999 } },
        React.createElement(Translation, null, (t) => t('MISSING_ACCESSORIAL_DOCS')))),
    MISSING_EQUIPMENT: (React.createElement(Tooltip, { id: "missing_equipment", style: { zIndex: 99999 } },
        React.createElement(Translation, null, (t) => t('NOT_APPLIED_TO_AN_EQUIPMENT')))),
    SHIPPER_CHANGED: (React.createElement(Tooltip, { id: "shipper_changed", style: { zIndex: 99999 } },
        React.createElement(Translation, null, (t) => t('SHIPPER_CHANGED_NOTE')))),
    NO_CARRIER_ASSIGNED_INFO: (React.createElement(Tooltip, { id: "no_carrier_assigned", style: { zIndex: 99999 } },
        React.createElement(Translation, null, (t) => t('NO_CARRIER_ASSIGNED_INFO_NOTE')))),
    EMPTY_TOOLTIP: React.createElement(Tooltip, { id: "empty_tooltip_for_buttons", content: "" }),
};
const helptext = {
    PROCESSED: 'ACCESSORIALS_TOOLTIP_PROCESSED',
    UNITS: 'ACCESSORIALS_TOOLTIP_UNITS',
    RATE: 'ACCESSORIALS_TOOLTIP_RATE',
    SHIPPER_NOTES: 'ACCESSORIALS_TOOLTIP_SHIPPER_NOTES',
    CARRIER_NOTES: 'ACCESSORIALS_TOOLTIP_CARRIER_NOTES',
};
function renderUnits(t, qty, referenceAccessorial) {
    if (!referenceAccessorial) {
        return '-';
    }
    return (React.createElement("div", { "data-testid": "div-e3a16c24", className: "" },
        React.createElement(CargomaticLabel, { dataTestId: "CargomaticLabel-ceb70659", helptext: t(helptext.UNITS), className: "mb-1" }, t('UNIT', { count: 3 })),
        React.createElement("div", { "data-testid": "div-617cf8ee" }, getUnitLabel(qty, referenceAccessorial.rate_type))));
}
function renderRate(t, rate, currency) {
    const rateDisplay = rate ? formatMoney(parseFloat(rate), currency) : '-';
    return (React.createElement("div", { "data-testid": "div-24578187", className: "" },
        React.createElement(CargomaticLabel, { dataTestId: "CargomaticLabel-3cbf7166", helptext: t(helptext.RATE), className: "mb-1" }, t('RATE_PER_UNIT')),
        React.createElement("div", { "data-testid": "div-b58d68e4" }, rateDisplay)));
}
function renderDeclineCode(t, declineReasonCode) {
    return (React.createElement("div", { "data-testid": "div-c26d72bd", className: "" },
        React.createElement(CargomaticLabel, { dataTestId: "CargomaticLabel-248103db", className: "mb-1" },
            titleize(t('REASON_CODE')),
            ":"),
        React.createElement("div", { "data-testid": "div-350702a5", className: "truncate" }, declineReasonCode)));
}
function renderNotes(t, note, isCarrier = false) {
    const notesHelptext = isCarrier
        ? helptext.CARRIER_NOTES
        : helptext.SHIPPER_NOTES;
    return (React.createElement("div", { "data-testid": "div-b1d131b8", className: "" },
        React.createElement(CargomaticLabel, { dataTestId: "CargomaticLabel-8e212bbf", helptext: t(notesHelptext), className: "mb-1" }, titleize(t('NOTE', { count: 3 }))),
        React.createElement("div", { "data-testid": "div-a606fe19", className: "truncate" }, note || '-')));
}
const AccessorialInfo = ({ accessorial, referenceAccessorial, actions, isCarrier, dataTestId, }) => {
    const { t } = useTranslation();
    const { rate, units, beginDate, endDate, location, description, notes } = getRequestFields(accessorial, isCarrier);
    const { currency, vendor, vendor_name, decline_reason_code: declineReasonCode, } = accessorial;
    return (React.createElement("div", { "data-testid": dataTestId, className: "" },
        React.createElement("div", { "data-testid": "div-5d4714ca", className: "flex flex-wrap justify-between" },
            React.createElement("div", { "data-testid": "div-789cfb7d", className: "w-3/4 flex -mx-2" },
                React.createElement("div", { "data-testid": "div-467616c7", className: "w-1/3 px-2" }, renderUnits(t, units, referenceAccessorial)),
                React.createElement("div", { "data-testid": "div-7b80a64e", className: "w-1/3 px-2" }, renderRate(t, rate, currency))),
            React.createElement("div", { "data-testid": "div-17102e4a", className: "w-1/4 px-2" }, actions())),
        React.createElement("div", { "data-testid": "div-ee43b029", className: "flex flex-wrap w-3/4 -mx-2" },
            isCarrier && vendor && (React.createElement("div", { "data-testid": "div-a35e73de", className: "w-1/3 px-2 mt-4" },
                React.createElement("label", { "data-testid": "label-8c7bb271", htmlFor: "", className: "block mb-1 form-label" }, titleize(t('VENDOR', { count: 1 }))),
                React.createElement("p", { "data-testid": "p-bc4a0aa4", className: "mb-0 text-sm" }, vendor_name))),
            beginDate && (React.createElement("div", { "data-testid": "div-5c5c61be", className: "w-1/3 px-2 mt-4" },
                React.createElement("label", { "data-testid": "label-10704074", htmlFor: "", className: "block mb-1 form-label" }, t('BEGIN_DATE')),
                React.createElement("p", { "data-testid": "p-f3b216e0", className: "mb-0 text-sm" }, moment(beginDate).format('MMM Do YYYY')),
                React.createElement("p", { "data-testid": "p-4f90f59b", className: "text-sm" }, moment(beginDate).format('hh:mm A')))),
            endDate && (React.createElement("div", { "data-testid": "div-27a5d17a", className: "w-1/3 px-2 mt-4" },
                React.createElement("label", { "data-testid": "label-8ca94da4", htmlFor: "", className: "block mb-1 form-label" }, t('END_DATE')),
                React.createElement("p", { "data-testid": "p-09e62149", className: "mb-0 text-sm" }, moment(endDate).format('MMM Do YYYY')),
                React.createElement("p", { "data-testid": "p-2f260081", className: "text-sm" }, moment(endDate).format('hh:mm A')))),
            location && (React.createElement("div", { "data-testid": "div-09896939", className: "w-1/3 px-2 mt-4" },
                React.createElement("label", { "data-testid": "label-51ea4f9c", className: "block mb-1 form-label" }, t('LOCATION')),
                React.createElement("p", { "data-testid": "p-ad7f5ce6" }, titleize(location)))),
            description && (React.createElement("div", { "data-testid": "div-67884963", className: "px-2 mt-4" },
                React.createElement("label", { "data-testid": "label-9570d608", htmlFor: "", className: "block mb-1 form-label" }, t('DESCRIPTION', { count: 1 })),
                React.createElement("p", { "data-testid": "p-ed58d238" }, description)))),
        declineReasonCode && (React.createElement("div", { "data-testid": "div-98f1773d", className: "mt-4" }, renderDeclineCode(t, declineReasonCode))),
        React.createElement("div", { "data-testid": "div-4f67244f", className: "mt-4" }, renderNotes(t, notes, isCarrier))));
};
const EditAccessorialForm = ({ referenceAccessorial, accessorial, isCarrier, onChange, onSave, onCancel, }) => {
    const { t } = useTranslation();
    const [label, info] = getLabelInfo(referenceAccessorial);
    const [errors, setErrors] = useState({
        carrier_rate: null,
        shipper_rate: null,
        carrier_unit_qty: null,
        shipper_unit_qty: null,
    });
    const { rateField, unitsField, beginDateField, endDateField, locationField, descriptionField, notesField, rate, units, beginDate, endDate, location, description, notes, } = getRequestFields(accessorial, isCarrier);
    function handleErrors(field, value) {
        setErrors((prev) => {
            let previous = Object.assign({}, prev);
            // Payee rates can be negative
            if (field === 'carrier_rate') {
                previous[field] = null;
            }
            else if (field === 'shipper_rate') {
                let parsedValue = parseFloat(value);
                previous[field] =
                    isNil(parsedValue) || parsedValue < 0
                        ? t('VALUE_MUST_BE_A_NUMBER_AND_CANNOT_BE_NEGATIVE')
                        : null;
            }
            else {
                let parsedValue = parseFloat(value);
                previous[field] =
                    !parsedValue || parsedValue < 0
                        ? t('VALUE_MUST_BE_A_NUMBER_AND_CANNOT_BE_NEGATIVE')
                        : null;
            }
            return previous;
        });
    }
    function handleChange(field) {
        return function (value) {
            const locationValueFields = ['shipper_location', 'location'];
            const positiveValueFields = [
                'carrier_rate',
                'shipper_rate',
                'carrier_unit_qty',
                'shipper_unit_qty',
            ];
            if (positiveValueFields.includes(field)) {
                handleErrors(field, value);
                onChange(field, value);
            }
            else if (locationValueFields.includes(field)) {
                onChange(field, value.value);
            }
            else {
                onChange(field, value);
            }
        };
    }
    function handleSave() {
        let errorsValidated = true;
        const errorsToCheck = Object.keys(errors);
        for (let i = 0; i < errorsToCheck.length; i++) {
            const item = errorsToCheck[i];
            if (errors[item]) {
                errorsValidated = false;
                break;
            }
        }
        if (errorsValidated)
            onSave();
    }
    const showBeginDate = referenceAccessorial.begin_date_required || beginDate;
    const showEndDate = referenceAccessorial.end_date_required || endDate;
    const showLocation = referenceAccessorial.location_required || location;
    const showDescription = referenceAccessorial.description_required || description;
    return (React.createElement("div", { "data-testid": "div-7da91118", className: "p-4 bg-gray-50" },
        React.createElement("div", { "data-testid": "div-1d3de2e3", className: "flex mb-4 -mx-2" },
            React.createElement("div", { "data-testid": "div-b0b4eef1", className: "w-1/3 px-2 mb-4" },
                React.createElement(CargomaticInput, { dataTestId: "CargomaticInput-a3020910", value: units, type: "number", label: t(label), info: t(info), placeholder: t('UNITS_PLACEHOLDER'), onChange: handleChange(unitsField), maxLength: 35 }),
                React.createElement(ErrorLabel, { dataTestId: "ErrorLabel-ea22ccb6", className: "mt-1" }, errors[unitsField])),
            React.createElement("div", { "data-testid": "div-e4f53b07", className: "px-2 mb-4 w-1/3" },
                React.createElement(CargomaticInput, { dataTestId: "CargomaticInput-701ee0c0", value: rate, type: "number", label: t('RATE_PER_UNIT'), info: t('ACCESSORIAL_RATE'), placeholder: t('RATE', { count: 1 }), onChange: handleChange(rateField), size: "20" }),
                React.createElement(ErrorLabel, { dataTestId: "ErrorLabel-9305067f", className: "mt-1" }, errors[rateField]))),
        React.createElement("div", { "data-testid": "div-103a7e17", className: "flex flex-wrap -mx-2" },
            showBeginDate && (React.createElement("div", { "data-testid": "div-824ddee0", className: "w-1/3 px-2 mb-4" },
                React.createElement("label", { "data-testid": "label-c692eeb5", htmlFor: "beginDate", className: "block mb-1 form-label" }, t('BEGIN_DATE')),
                React.createElement(DatePicker, { id: "beginDate", selected: getMomentDate(beginDate), onChange: handleChange(beginDateField), customInput: React.createElement(DateIconButton, { dataTestId: "DateIconButton-117a097b" }), shouldCloseOnSelect: true, dateFormat: DATE_FORMAT, showTimeSelect: true, timeIntervals: 5 }))),
            showEndDate && (React.createElement("div", { "data-testid": "div-80400f2a", className: "w-1/3 px-2 mb-4" },
                React.createElement("label", { "data-testid": "label-36458e54", htmlFor: "endDate", className: "block mb-1 form-label" }, t('END_DATE')),
                React.createElement(DatePicker, { id: "endDate", selected: getMomentDate(endDate), onChange: handleChange(endDateField), customInput: React.createElement(DateIconButton, { dataTestId: "DateIconButton-f19e3a72" }), dateFormat: DATE_FORMAT, shouldCloseOnSelect: true, showTimeSelect: true, timeIntervals: 5 }))),
            showLocation && (React.createElement("div", { "data-testid": "div-5af39733", className: "w-1/3 px-2 mb-4" },
                React.createElement(CargomaticSelect, { dataTestId: "CargomaticSelect-a9729c71", selected: location, fullWidth: false, items: locationTypes, defaultSelectLabel: "", label: t('LOCATION'), info: t('LOCATION_CUSTOMER_OR_TERMINAL'), onSelect: handleChange(locationField), showCaption: false, valueKey: "value", textKey: "localizationSlug" })))),
        showDescription && (React.createElement("div", { "data-testid": "div-2fba806f", className: "flex -mx-2" },
            React.createElement("div", { "data-testid": "div-8c413d30", className: "w-1/2 px-2 mb-4" },
                React.createElement(CargomaticInput, { dataTestId: "CargomaticInput-a9b1f3b2", value: description, type: "text", label: t('DESCRIPTION', { count: 1 }), placeholder: t('DESCRIPTION', { count: 1 }), onChange: handleChange(descriptionField) })))),
        React.createElement("div", { "data-testid": "div-3ea69e7d", className: "w-1/2 mb-4" },
            React.createElement(CargomaticInput, { dataTestId: "CargomaticInput-a8d3aebf", value: notes, label: titleize(t('NOTE', { count: 3 })), info: titleize(t('NOTE', { count: 3 })), type: "textarea", placeholder: titleize(t('NOTE', { count: 3 })), onChange: handleChange(notesField) })),
        React.createElement("div", { "data-testid": "div-55bd5128", className: "mt-6" },
            React.createElement("div", { "data-testid": "div-d855d4f3", className: "flex items-center justify-end -mx-2" },
                React.createElement("div", { "data-testid": "div-3e92eaad", className: "px-2" },
                    React.createElement("button", { "data-testid": "button-70262908", type: "button", className: "button button-green", title: titleize(t('SAVE')), onClick: handleSave }, titleize(t('SAVE')))),
                React.createElement("div", { "data-testid": "div-65cb51ad", className: "px-2" },
                    React.createElement("button", { "data-testid": "button-0d6cf1a1", type: "button", title: t('CANCEL_EDIT'), className: "button button-secondary", onClick: onCancel }, t('CANCEL')))))));
};
class AccessorialRequestPane extends Component {
    constructor(props) {
        var _a, _b;
        super(props);
        this.fetchEquipmentsById = async (equipIds = []) => {
            var _a, _b;
            if (equipIds.length) {
                try {
                    const result = await getAllEquipmentsByEquipId(equipIds);
                    if ((_a = result === null || result === void 0 ? void 0 : result.entity) === null || _a === void 0 ? void 0 : _a.success) {
                        const equipments = (_b = result.entity) === null || _b === void 0 ? void 0 : _b.results;
                        if (equipments.length) {
                            const allEquipmentsById = equipments.reduce((acc, { equipment_id, _id }) => {
                                if (!acc[equipment_id]) {
                                    acc[equipment_id] = _id;
                                }
                                return acc;
                            }, {});
                            this.setState({ allEquipmentsById });
                        }
                    }
                }
                catch (e) {
                    showToast(e.message, 'FAILED_FETCHING_EQUIPMENTS', 'error');
                }
            }
        };
        this.getAllEquipmentsId = (stops = []) => {
            if (stops.length) {
                const allEquipmentsById = stops.reduce((acc, stop) => {
                    var _a;
                    (_a = stop === null || stop === void 0 ? void 0 : stop.equipments) === null || _a === void 0 ? void 0 : _a.forEach(({ equipment_id, _id }) => {
                        if (!acc[equipment_id]) {
                            acc[equipment_id] = _id;
                        }
                    });
                    return acc;
                }, {});
                this.setState({ allEquipmentsById });
            }
        };
        this.fetchAccessorial = async () => {
            var _a, _b;
            try {
                const { accessorial: { accessorial_type: slug }, } = this.state;
                const query = { slug, paginate: false };
                const result = await getAccessorials(query);
                if ((_b = (_a = result === null || result === void 0 ? void 0 : result.entity) === null || _a === void 0 ? void 0 : _a.items) === null || _b === void 0 ? void 0 : _b.length) {
                    this.setState({
                        accessorialFetched: true,
                        referenceAccessorial: Object.assign({}, result.entity.items[0]),
                    });
                    this.fetchDocuments();
                }
            }
            catch (e) {
                console.error(e);
            }
        };
        this.fetchDocuments = async () => {
            var _a, _b;
            if (this.state.accessorial) {
                const { data, errors } = await getDocumentsV2([
                    this.state.accessorial._id,
                ]);
                if (errors) {
                    this.showGetDocumentsErrorToast(errors[0].message);
                    return;
                }
                const documents = (_a = data === null || data === void 0 ? void 0 : data.getDocumentsV2) === null || _a === void 0 ? void 0 : _a.documents;
                if (!isEqual(this.state.documents, documents)) {
                    this.setState({ documents: (_b = data === null || data === void 0 ? void 0 : data.getDocumentsV2) === null || _b === void 0 ? void 0 : _b.documents });
                }
            }
        };
        this.showGetDocumentsErrorToast = (err) => {
            const { t } = this.props;
            showToast(t('GET_DOCUMENT_ERROR'), t('GET_DOCUMENT_FAILED_WITH_ERROR', { error: err }), 'error');
        };
        this.onAccessorialExceptionsStoreChange = () => {
            var _a, _b, _c;
            const accessorials = ExceptionStore.getAllAccessorialRequests();
            const selectedAccessorial = _.find(accessorials, {
                _id: this.props.accessorial,
            });
            // Handle loading states
            if (selectedAccessorial) {
                // For edit operations
                if (this.state.isEditSaving) {
                    const currentEditableFields = _.pick(this.state, editableFieldsWhitelist);
                    const updatedEditableFields = _.pick(selectedAccessorial, editableFieldsWhitelist);
                    // Clear loading state if fields changed (success) or didn't change (likely failure)
                    // The store emits changes on both success and failure now
                    if (!isEqual(currentEditableFields, updatedEditableFields)) {
                        // Success - fields changed
                        this.setState({ isEditSaving: false });
                        // Refresh shipment data to ensure all components have latest info
                        if ((_a = this.props.shipment) === null || _a === void 0 ? void 0 : _a._id) {
                            ShipmentsActionCreators.getShipment(this.props.shipment._id, true);
                        }
                    }
                    else {
                        // Possible failure or no-op - clear loading state after a short delay
                        // to ensure this isn't just the initial store update
                        this.editSavingTimeoutId = setTimeout(() => {
                            if (this.state.isEditSaving) {
                                this.setState({ isEditSaving: false });
                            }
                        }, 100);
                    }
                }
                // For approve/decline operations - always clear on store change
                // since the store now emits changes on both success and failure
                if (this.state.isApproveSaving || this.state.isDeclineSaving) {
                    this.setState({
                        isApproveSaving: false,
                        isDeclineSaving: false,
                    });
                }
            }
            else if (this.state.isEditSaving ||
                this.state.isApproveSaving ||
                this.state.isDeclineSaving) {
                // If accessorial not found but we're in a saving state,
                // this is likely a failure scenario - clear loading states
                this.setState({
                    isEditSaving: false,
                    isApproveSaving: false,
                    isDeclineSaving: false,
                });
            }
            if (this.state.editCarrierRate ||
                this.state.editShipperRate ||
                !this.props.showAccessorialRequestPane) {
                return null;
            }
            if (!selectedAccessorial) {
                // tbrown 08-12-20 Leaving this here as a reminder to fix the multiple instance problem.
                console.log(`Could not find accessorial ${this.props.accessorial} in ExceptionStore.getAllAccessorialRequests()`);
                return;
            }
            const newState = omitBy(pick(selectedAccessorial, whitelist), (value) => !value);
            let currentState = pick(this.state, whitelist);
            currentState = omitBy(currentState, (value) => !value);
            if (isEqual(newState, currentState)) {
                return;
            }
            const containers = ((_b = this.props.shipment) === null || _b === void 0 ? void 0 : _b.equipments) || [];
            let selectedEquipment = null;
            if ((containers === null || containers === void 0 ? void 0 : containers.length) &&
                ((_c = newState.equipment_id) === null || _c === void 0 ? void 0 : _c.length) &&
                containers.includes(newState === null || newState === void 0 ? void 0 : newState.equipment_id[0])) {
                selectedEquipment = newState.equipment_id[0];
            }
            this.setState(Object.assign(Object.assign({}, newState), { selectedEquipment, accessorial: selectedAccessorial, accessorialFetched: false }), () => this.fetchAccessorial());
        };
        this.canEditProtectedAccessorial = () => {
            const userRoles = SessionStore.getUser().roles;
            return userRoles.includes(Roles.FINANCE_ADMIN);
        };
        // Approve and Decline
        this.handleRequestAction = (type, action) => {
            const { t, accessorial: accessorialId, shipment } = this.props;
            const { accessorial, declineNote, declineReasonCode, errors } = this.state;
            // Set loading state based on action
            if (action === 'approve') {
                this.setState({ isApproveSaving: true });
            }
            else if (action === 'decline') {
                this.setState({ isDeclineSaving: true });
            }
            const isVendorPaid = (accessorial === null || accessorial === void 0 ? void 0 : accessorial.carrier_status) === 'approved' &&
                isVendorChargePaid(accessorialId, shipment === null || shipment === void 0 ? void 0 : shipment.vendor_payables);
            const payload = {
                resolution_type: action,
            };
            if (type === USER_TYPES.CARRIER) {
                payload.sendEmail = true;
                if (action === 'decline') {
                    if (!declineNote || declineNote === '') {
                        errors.declineNote = new Error(t('PROVIDE_DECLINE_NOTE'));
                        return this.setState({ errors });
                    }
                    if (accessorial.meta) {
                        accessorial.meta.carrier_note = declineNote;
                    }
                    else {
                        accessorial.meta = {
                            carrier_note: declineNote,
                        };
                    }
                    switch (accessorial.payee_type) {
                        case USER_TYPES.CARRIER:
                            accessorial.declineCarrier = true;
                            accessorial.carrierDeclineReasonCode = declineReasonCode;
                            break;
                        case USER_TYPES.VENDOR:
                            accessorial.declineVendor = !isVendorPaid;
                            break;
                        case USER_TYPES.PARTNER:
                            accessorial.declinePartnerPayee = true;
                            break;
                        default:
                            accessorial.declineCarrier = true;
                            break;
                    }
                    payload.accessorials = [accessorial];
                    AccessorialRequestActionCreators.declineAccessorials(payload);
                }
                else if (action === 'approve') {
                    switch (accessorial.payee_type) {
                        case USER_TYPES.CARRIER:
                            accessorial.approveCarrier = true;
                            break;
                        case USER_TYPES.VENDOR:
                            accessorial.approveVendor = !isVendorPaid;
                            break;
                        case USER_TYPES.PARTNER:
                            accessorial.approvePartnerPayee = true;
                            break;
                        default:
                            accessorial.approveCarrier = true;
                            break;
                    }
                    payload.accessorials = [accessorial];
                    AccessorialRequestActionCreators.approveAccessorials(payload);
                }
            }
            if (type === 'shipper') {
                if (action === 'decline') {
                    if (accessorial.payer_type === USER_TYPES.CONSIGNEE) {
                        accessorial.declineConsigneePayer = true;
                    }
                    else {
                        accessorial.declineShipper = true;
                    }
                    if (!declineNote || declineNote === '') {
                        errors.declineNote = new Error(t('PROVIDE_DECLINE_NOTE'));
                        return this.setState({ errors });
                    }
                    if (accessorial.meta) {
                        accessorial.meta.shipper_note = declineNote;
                    }
                    else {
                        accessorial.meta = {
                            shipper_note: declineNote,
                        };
                    }
                    payload.accessorials = [accessorial];
                    AccessorialRequestActionCreators.declineAccessorials(payload);
                }
                else if (action === 'approve') {
                    if (accessorial.payer_type === USER_TYPES.CONSIGNEE) {
                        accessorial.approveConsigneePayer = true;
                    }
                    else {
                        accessorial.approveShipper = true;
                    }
                    payload.accessorials = [accessorial];
                    AccessorialRequestActionCreators.approveAccessorials(payload);
                }
            }
            this.setState({
                showConfirmDeclineModal: false,
                declineNote: '',
                declineReasonCode: null,
            });
        };
        // Approve All and Decline All
        this.handleBulkActionMenu = (event) => {
            this.setState({
                showConfirmDeclineBulkModal: true,
                event,
            });
        };
        this.handleConfirmBulk = () => {
            const { t, accessorial: accessorialId, shipment } = this.props;
            const { accessorial, event, declineNote, declineReasonCode, errors } = this.state;
            // Set loading state based on action
            if (event === 'approve-all') {
                this.setState({ isApproveSaving: true });
            }
            else if (event === 'decline-all') {
                this.setState({ isDeclineSaving: true });
            }
            const isVendorPaid = (accessorial === null || accessorial === void 0 ? void 0 : accessorial.carrier_status) === 'approved' &&
                isVendorChargePaid(accessorialId, shipment === null || shipment === void 0 ? void 0 : shipment.vendor_payables);
            if (event) {
                if (event === 'approve-all') {
                    if (accessorial.payee_type === USER_TYPES.VENDOR) {
                        accessorial.approveVendor = !isVendorPaid;
                    }
                    else if (accessorial.payee_type === USER_TYPES.PARTNER) {
                        accessorial.approvePartnerPayee = true;
                    }
                    else {
                        accessorial.approveCarrier = true;
                    }
                    if (accessorial.payer_type === USER_TYPES.CONSIGNEE) {
                        accessorial.approveConsigneePayer = true;
                    }
                    else {
                        accessorial.approveShipper = true;
                    }
                    const payload = {
                        accessorials: [accessorial],
                        resolution_type: 'approve',
                        sendEmail: true,
                    };
                    AccessorialRequestActionCreators.approveAccessorials(payload);
                }
                else if (event === 'decline-all') {
                    if (!declineNote || declineNote === '') {
                        errors.declineNote = new Error(t('PROVIDE_DECLINE_NOTE'));
                        return this.setState({ errors });
                    }
                    if (declineReasonCode) {
                        accessorial.carrierDeclineReasonCode = declineReasonCode;
                    }
                    if (accessorial.meta) {
                        accessorial.meta.shipper_note = declineNote;
                        accessorial.meta.carrier_note = declineNote;
                    }
                    else {
                        accessorial.meta = {
                            shipper_note: declineNote,
                            carrier_note: declineNote,
                        };
                    }
                    if (accessorial.payer_type === USER_TYPES.CONSIGNEE) {
                        accessorial.declineConsigneePayer = true;
                    }
                    else if (accessorial.payee_type === USER_TYPES.VENDOR) {
                        accessorial.declineVendor = !isVendorPaid;
                    }
                    else {
                        accessorial.declineCarrier = true;
                    }
                    if (accessorial.payee_type === USER_TYPES.PARTNER) {
                        accessorial.declinePartnerPayee = true;
                    }
                    else {
                        accessorial.declineShipper = true;
                    }
                    const payload = {
                        accessorials: [accessorial],
                        resolution_type: 'decline',
                        sendEmail: true,
                    };
                    AccessorialRequestActionCreators.declineAccessorials(payload);
                }
                this.setState({
                    showConfirmDeclineBulkModal: false,
                    declineNote: '',
                    declineReasonCode: null,
                });
            }
            return null;
        };
        this.handleEquipmentSelect = async (selected, equipmentId) => {
            if (selected) {
                return null;
            }
            const { _id } = this.state;
            const updated = { _id };
            updated.equipment = [equipmentId];
            AccessorialRequestActionCreators.updateAccessorialException([updated]);
        };
        this.invalidUnitQty = (field, value) => field.indexOf('unit_qty') > -1 && !validDecimal(value);
        this.handleChange = (field, value) => {
            if (!this.invalidUnitQty(field, value)) {
                this.setState({ [field]: value });
            }
        };
        this.renderAcceptButton = (type, disabled = false) => {
            return (React.createElement(ApproveButton, { dataTestId: "ApproveButton-c6f695d8", disabled: disabled, onClick: () => {
                    this.setState({
                        showConfirmDeclineModal: true,
                        confirmType: type,
                        confirmDecline: false,
                    });
                } }));
        };
        this.renderDeclineButton = (type, disabled = false) => {
            return (React.createElement(DeclineButton, { dataTestId: "DeclineButton-09f71576", disabled: disabled, onClick: () => {
                    this.setState({
                        showConfirmDeclineModal: true,
                        confirmType: type,
                        confirmDecline: true,
                    });
                } }));
        };
        this.userCanApproveAccessorial = (accessorial) => {
            const user = SessionStore.getUser();
            return userCanApproveAccessorialsHelper(user, [accessorial]);
        };
        this.checkMissingDocsByUserType = (accessorial, type) => {
            if (type === USER_TYPES.CARRIER) {
                return !!(accessorial === null || accessorial === void 0 ? void 0 : accessorial.missing_docs);
            }
            return (!!(accessorial === null || accessorial === void 0 ? void 0 : accessorial.missing_docs) ||
                (accessorial === null || accessorial === void 0 ? void 0 : accessorial.customer_approval_provided) === false);
        };
        this.confirmAction = () => {
            const { confirmType, confirmDecline } = this.state;
            const strAdminAction = confirmDecline ? 'decline' : 'approve';
            this.handleRequestAction(confirmType, strAdminAction);
            this.setState({ showConfirmDeclineModal: false });
        };
        this.handleDeclineNote = (value) => {
            const { errors } = this.state;
            if (value && value !== '') {
                errors.declineNote = {};
            }
            this.setState({ declineNote: value, errors });
        };
        this.handleDeclineReasonCode = (value) => {
            if (value) {
                this.setState({ declineReasonCode: value });
            }
        };
        const accessorials = ExceptionStore.getAllAccessorialRequests();
        const accessorial = _.find(accessorials, { _id: this.props.accessorial });
        const defaultState = Object.assign(Object.assign({ begin_datetime: null, end_datetime: null, vendor: null, vendor_name: '', location: null, description: null, shipper_begin_datetime: null, shipper_end_datetime: null, shipper_location: null, shipper_description: null }, accessorial), { originalAccessorial: accessorial });
        const containers = ((_a = props.shipment) === null || _a === void 0 ? void 0 : _a.equipments) || [];
        let selectedEquipment = null;
        if (accessorial &&
            containers.length &&
            ((_b = accessorial.equipment) === null || _b === void 0 ? void 0 : _b.length) &&
            containers.includes(accessorial.equipment_id[0])) {
            selectedEquipment = accessorial.equipment_id[0];
        }
        this.state = Object.assign(Object.assign({ accessorialFetched: false, accesorial: null, referenceAccessorial: null, showConfirmDeclineBulkModal: false, editCarrierRate: false, editShipperRate: false, isEditSaving: false, isApproveSaving: false, isDeclineSaving: false, selectedEquipment: selectedEquipment || [], shipmentEquipment: containers || [], showConfirmDeclineModal: false, isAccessorialsAdmin: SessionStore.isAccessorialsAdmin(), accessorial, confirmType: null, confirmDecline: null, declineNote: '', declineReasonCode: null }, defaultState), { errors: {
                declineNote: {},
            }, documents: [] });
        this.editSavingTimeoutId = null;
    }
    componentDidMount() {
        var _a, _b;
        this.fetchEquipmentsById((_b = (_a = this.props) === null || _a === void 0 ? void 0 : _a.shipment) === null || _b === void 0 ? void 0 : _b.equipments);
        ExceptionStore.addChangeListener(this.onAccessorialExceptionsStoreChange);
        this.fetchAccessorial();
    }
    componentWillUnmount() {
        ExceptionStore.removeChangeListener(this.onAccessorialExceptionsStoreChange);
        if (this.editSavingTimeoutId) {
            clearTimeout(this.editSavingTimeoutId);
        }
    }
    handleEditToggle(type) {
        if (type === USER_TYPES.CARRIER) {
            this.setState((prevState) => {
                return {
                    editCarrierRate: !prevState.editCarrierRate,
                };
            });
        }
        else {
            this.setState((prevState) => {
                return {
                    editShipperRate: !prevState.editShipperRate,
                };
            });
        }
    }
    getRateSource(originalRateSource, original_rate, updated_rate) {
        if ((original_rate && updated_rate && original_rate === updated_rate) ||
            originalRateSource === 'overwritten_price_engine')
            return originalRateSource;
        if (originalRateSource === 'automated') {
            return 'overwritten_price_engine';
        }
        return 'manual';
    }
    handleSaveButton(type) {
        const { accessorial, _id } = this.state;
        // Set loading state to prevent race conditions
        this.setState({ isEditSaving: true });
        let updated = _.pick(this.state, 'shipper_rate', 'shipper_unit_qty', 'admin_note', 'carrier_note', 'shipper_note', 'carrier_rate', 'carrier_unit_qty', 'location', 'begin_datetime', 'end_datetime', 'description', 'shipper_location', 'shipper_begin_datetime', 'shipper_end_datetime', 'shipper_description');
        // Find which values have changed
        const original = _.pick(accessorial, 'shipper_rate', 'shipper_unit_qty', 'carrier_rate', 'carrier_unit_qty', 'rate_source', 'receivable_rate_source', 'payable_rate_source');
        if (!original.shipper_rate && updated.shipper_rate) {
            original.shipper_rate = 0;
        }
        if (updated.shipper_note && updated.shipper_note.trim().length < 1) {
            delete updated.shipper_note;
        }
        if (updated.carrier_note && updated.carrier_note.trim().length < 1) {
            delete updated.carrier_note;
        }
        if (type === USER_TYPES.SHIPPER) {
            updated = Object.assign({ _id }, _.pick(updated, 'shipper_rate', 'shipper_unit_qty', 'shipper_note', 'shipper_begin_datetime', 'shipper_end_datetime', 'shipper_location', 'shipper_description'));
            if (updated.shipper_rate > 0 &&
                !usingValidDecimals(updated.shipper_rate)) {
                updated.shipper_rate = parseFloat(updated.shipper_rate).toFixed(2);
            }
            if ((updated.shipper_rate > 0 &&
                !usingValidDecimals(updated.shipper_rate)) ||
                updated.shipper_note) {
                updated.shipper_status = 'pending-shipper';
            }
            if (updated.carrier_rate) {
                updated.rate_source = this.getRateSource(original.rate_source, original.carrier_rate, updated.carrier_rate);
                updated.payable_rate_source = this.getRateSource(original.payable_rate_source, original.carrier_rate, updated.carrier_rate);
            }
            if (updated.shipper_rate) {
                updated.rate_source = this.getRateSource(original.rate_source, original.shipper_rate, updated.shipper_rate);
                updated.receivable_rate_source = this.getRateSource(original.receivable_rate_source, original.shipper_rate, updated.shipper_rate);
            }
        }
        else {
            updated = Object.assign({ _id }, _.pick(updated, 'carrier_rate', 'carrier_unit_qty', 'carrier_note', 'begin_datetime', 'end_datetime', 'location', 'description'));
            if (!updated.location) {
                delete updated.location;
            }
            if (!updated.begin_datetime) {
                delete updated.begin_datetime;
            }
            if (!updated.end_datetime) {
                delete updated.end_datetime;
            }
            if (!updated.shipper_location) {
                delete updated.shipper_location;
            }
            if (!updated.shipper_begin_datetime) {
                delete updated.shipper_begin_datetime;
            }
            if (!updated.shipper_end_datetime) {
                delete updated.shipper_end_datetime;
            }
            if (!updated.description) {
                delete updated.description;
            }
            if (!updated.shipper_description) {
                delete updated.shipper_description;
            }
            if (updated.carrier_rate > 0 &&
                !usingValidDecimals(updated.carrier_rate)) {
                updated.carrier_rate = parseFloat(updated.carrier_rate).toFixed(2);
            }
            if (updated.carrier_rate || updated.shipper_rate) {
                updated.rate_source = this.getRateSource(original.rate_source);
            }
            if (updated.carrier_rate) {
                updated.rate_source = this.getRateSource(original.rate_source, original.carrier_rate, updated.carrier_rate);
                updated.payable_rate_source = this.getRateSource(original.payable_rate_source, original.carrier_rate, updated.carrier_rate);
            }
            if (updated.shipper_rate) {
                updated.rate_source = this.getRateSource(original.rate_source, original.shipper_rate, updated.shipper_rate);
                updated.receivable_rate_source = this.getRateSource(original.receivable_rate_source, original.shipper_rate, updated.shipper_rate);
            }
            if ((updated.carrier_rate > 0 &&
                !usingValidDecimals(updated.carrier_rate)) ||
                updated.carrier_note) {
                updated.carrier_status = 'pending-admin';
            }
        }
        if (updated &&
            typeof updated === 'object' &&
            Object.keys(updated).length > 1) {
            // Ensure there's actual updates
            AccessorialRequestActionCreators.updateAccessorialException([updated]);
        }
        this.handleEditToggle(type);
    }
    renderEditShipperRequestForm() {
        const { editShipperRate } = this.state;
        if (!editShipperRate) {
            return null;
        }
        const { shipper_rate, shipper_unit_qty, shipper_note, shipper_begin_datetime, shipper_end_datetime, shipper_location, shipper_description, referenceAccessorial, carrier_status, shipper_status, } = this.state;
        const accessorial = {
            shipper_rate,
            shipper_unit_qty,
            shipper_begin_datetime,
            shipper_end_datetime,
            shipper_location,
            shipper_description,
            shipper_note,
        };
        const type = USER_TYPES.SHIPPER;
        return (React.createElement(EditAccessorialForm, { carrier_status: carrier_status, shipper_status: shipper_status, isCarrier: false, accessorial: accessorial, referenceAccessorial: referenceAccessorial, onChange: this.handleChange, onSave: () => this.handleSaveButton(type), onCancel: () => this.handleEditToggle(type) }));
    }
    renderShipperRequestRow() {
        if (this.state.editShipperRate) {
            return this.renderEditShipperRequestForm();
        }
        return this.renderShipperRequestInfo();
    }
    renderShipperRequestInfo() {
        var _a;
        const { editShipperRate } = this.state;
        if (editShipperRate) {
            return null;
        }
        const { shipper_unit_qty, shipper_rate, shipper_description, shipper_location, shipper_begin_datetime, shipper_end_datetime, shipper_note, referenceAccessorial, accessorial, } = this.state;
        const currentAccessorial = {
            shipper_unit_qty,
            shipper_rate,
            shipper_description,
            shipper_location,
            shipper_begin_datetime,
            shipper_end_datetime,
            shipper_note,
            currency: (_a = this.state.accessorial) === null || _a === void 0 ? void 0 : _a.currency,
            decline_reason_code: accessorial.shipper_decline_reason_code,
        };
        return (React.createElement(AccessorialInfo, { dataTestId: 'div-00353f47', isCarrier: false, accessorial: currentAccessorial, referenceAccessorial: referenceAccessorial, actions: () => this.renderActions(USER_TYPES.SHIPPER) }));
    }
    renderCarrierRequestRow() {
        if (this.state.editCarrierRate) {
            return this.renderEditCarrierRequestForm();
        }
        return this.renderCarrierRequestInfo();
    }
    renderEditCarrierRequestForm() {
        const { carrier_rate, carrier_unit_qty, carrier_note, begin_datetime, end_datetime, location, description, referenceAccessorial, shipper_status, carrier_status, } = this.state;
        const accessorial = {
            carrier_rate,
            carrier_unit_qty,
            begin_datetime,
            end_datetime,
            location,
            description,
            carrier_note,
        };
        const type = USER_TYPES.CARRIER;
        return (React.createElement(EditAccessorialForm, { isCarrier: true, carrier_status: carrier_status, shipper_status: shipper_status, accessorial: accessorial, referenceAccessorial: referenceAccessorial, onChange: this.handleChange, onSave: () => this.handleSaveButton(type), onCancel: () => this.handleEditToggle(type) }));
    }
    renderCarrierRequestInfo() {
        var _a;
        const { carrier_unit_qty, carrier_rate, carrier_note, begin_datetime, end_datetime, location, description, vendor, vendor_name, referenceAccessorial, accessorial, } = this.state;
        const currentAccessorial = {
            carrier_unit_qty,
            carrier_rate,
            carrier_note,
            begin_datetime,
            end_datetime,
            location,
            description,
            vendor,
            vendor_name,
            decline_reason_code: accessorial.carrier_decline_reason_code,
            currency: (_a = this.state.accessorial) === null || _a === void 0 ? void 0 : _a.currency,
        };
        return (React.createElement(AccessorialInfo, { dataTestId: 'div-233f3844', isCarrier: true, accessorial: currentAccessorial, referenceAccessorial: referenceAccessorial, actions: () => this.renderActions(USER_TYPES.CARRIER) }));
    }
    renderEquipment() {
        const { shipmentEquipment, selectedEquipment, allEquipmentsById = {}, } = this.state;
        if (!shipmentEquipment)
            return null;
        return shipmentEquipment.map((eq, index) => {
            let selected = false;
            if (selectedEquipment === eq) {
                selected = true;
            }
            return (React.createElement("button", { "data-testid": `button-9863314a-${index}`, onClick: () => this.handleEquipmentSelect(selected, allEquipmentsById[eq]), className: "px-2 py-2 mr-2 bg-transparent rounded", style: selected
                    ? { border: '1px solid #21b88e', color: '#21b88e' }
                    : { border: '1px solid #9e9e9e', color: '#9e9e9e' }, key: eq.id },
                React.createElement("span", { "data-testid": `span-fe63be10-${index}` }, eq),
                selected && (React.createElement("span", { "data-testid": `span-48915b03-${index}` },
                    React.createElement("i", { "data-testid": `i-caf83b36-${index}`, className: "ml-2 fa fa-check-circle-o", style: { color: '#21b88e' } })))));
        });
    }
    getToolTip(buttonType, isMissingEquipment = false, declinedAccessorial = false, isCarrierMissing = false) {
        if (declinedAccessorial) {
            return TOOLTIPS.SHIPPER_CHANGED;
        }
        if (isCarrierMissing) {
            return TOOLTIPS.NO_CARRIER_ASSIGNED_INFO;
        }
        if (isMissingEquipment && buttonType === 'approve') {
            return TOOLTIPS.MISSING_EQUIPMENT;
        }
        return TOOLTIPS.EMPTY_TOOLTIP;
    }
    renderActions(type) {
        var _a, _b;
        const { accessorial, isAccessorialsAdmin, isEditSaving, isApproveSaving, isDeclineSaving, } = this.state;
        const { shipment, accessorial: accessorialId } = this.props;
        const isAnySaving = isEditSaving || isApproveSaving || isDeclineSaving;
        const isVendorPaid = (accessorial === null || accessorial === void 0 ? void 0 : accessorial.carrier_status) === 'approved' &&
            isVendorChargePaid(accessorialId, shipment === null || shipment === void 0 ? void 0 : shipment.vendor_payables) &&
            type === USER_TYPES.CARRIER;
        const shipmentCarrier = ((_a = shipment === null || shipment === void 0 ? void 0 : shipment.carrier) === null || _a === void 0 ? void 0 : _a._id) || (shipment === null || shipment === void 0 ? void 0 : shipment.carrier);
        const carrierAssociatedWithAccessorial = ((_b = accessorial.carrier) === null || _b === void 0 ? void 0 : _b._id) || accessorial.carrier;
        const carrierMismatch = USER_TYPES.CARRIER === type &&
            accessorial.payee_type === 'carrier' &&
            shipmentCarrier !== carrierAssociatedWithAccessorial;
        // Check if accessorial status is already in final state for this user type
        const isAlreadyApproved = type === USER_TYPES.CARRIER
            ? accessorial.carrier_status === 'approved'
            : accessorial.shipper_status === 'approved';
        const isAlreadyDeclined = type === USER_TYPES.CARRIER
            ? accessorial.carrier_status === 'declined'
            : accessorial.shipper_status === 'declined';
        // Only disable approve when carrier mismatches OR status is already approved
        // For decline, only disable if payable is already processed OR already declined (allow declining approved accessorials)
        const disablePayeeApprove = (carrierMismatch && USER_TYPES.CARRIER === type) || isAlreadyApproved;
        const disablePayeeDecline = (carrierMismatch &&
            USER_TYPES.CARRIER === type &&
            accessorialPaid(shipment, accessorial)) ||
            isAlreadyDeclined;
        // Edit button should only be disabled for carrier mismatches, not for approved status
        const disablePayeeEdit = carrierMismatch && USER_TYPES.CARRIER === type;
        const processCheck = type === USER_TYPES.SHIPPER ? accessorialCanBeInvoiced : accessorialPaid;
        const isProcessed = processCheck(shipment, accessorial);
        const isEditable = accessorial.editable ||
            accessorial.editable === undefined ||
            this.canEditProtectedAccessorial();
        const userCanApprove = this.userCanApproveAccessorial(accessorial, type);
        const isMissingDocs = this.checkMissingDocsByUserType(accessorial, type);
        const isMissingEquipment = checkIfEquipmentIsMissingForAccessorial(accessorial);
        const declinedAccessorial = checkWhichSideAccessorialAutoDeclined(accessorial, type);
        const isCarrierMissing = type === 'carrier' ? isCarrierMissingInAccessorial(accessorial) : false;
        const accessorialApprovalAllowed = () => {
            if (userCanApprove && isAccessorialsAdmin) {
                return true;
            }
            return userCanApprove && !isMissingDocs;
        };
        if (isMissingEquipment || declinedAccessorial || isCarrierMissing) {
            return (React.createElement("div", { "data-testid": "div-7704ff1512", className: "flex justify-end -mx-1" },
                React.createElement(OverlayTrigger, { placement: "top", overlay: this.getToolTip('edit', isMissingEquipment, declinedAccessorial, isCarrierMissing) },
                    React.createElement("div", { "data-testid": "div-4381acd111", className: "px-1" }, this.renderEditButton(type, isShipmentCanceled(shipment) ||
                        isProcessed ||
                        disablePayeeEdit ||
                        !this.state.accessorialFetched ||
                        !isEditable ||
                        isVendorPaid ||
                        declinedAccessorial ||
                        isCarrierMissing ||
                        isAnySaving))),
                React.createElement(OverlayTrigger, { placement: "top", overlay: this.getToolTip('approve', isMissingEquipment, declinedAccessorial, isCarrierMissing) },
                    React.createElement("div", { "data-testid": "div-1ee7bce911", className: "px-1" }, this.renderAcceptButton(type, isShipmentCanceled(shipment) ||
                        isProcessed ||
                        disablePayeeApprove ||
                        !this.state.accessorialFetched ||
                        !userCanApprove ||
                        isVendorPaid ||
                        isMissingEquipment ||
                        declinedAccessorial ||
                        isCarrierMissing ||
                        isAnySaving))),
                React.createElement(OverlayTrigger, { placement: "top", overlay: this.getToolTip('declined', isMissingEquipment, declinedAccessorial, isCarrierMissing) },
                    React.createElement("div", { "data-testid": "div-4ac0419fsw2", className: "px-1" }, this.renderDeclineButton(type, isShipmentCanceled(shipment) ||
                        isProcessed ||
                        disablePayeeDecline ||
                        isVendorPaid ||
                        declinedAccessorial ||
                        isCarrierMissing ||
                        isAnySaving)))));
        }
        if (isProcessed) {
            return (React.createElement(OverlayTrigger, { placement: "top", overlay: TOOLTIPS.PROCESSED },
                React.createElement("div", { "data-testid": "div-cc89bbd8", className: "flex justify-end -mx-1" },
                    React.createElement("div", { "data-testid": "div-1f9e2468", className: "px-1" }, this.renderEditButton(type, isShipmentCanceled(shipment) ||
                        isProcessed ||
                        disablePayeeEdit ||
                        !isEditable ||
                        isVendorPaid ||
                        isEditSaving)),
                    React.createElement("div", { "data-testid": "div-bd11f4e4", className: "px-1" }, this.renderAcceptButton(type, isShipmentCanceled(shipment) ||
                        isProcessed ||
                        disablePayeeApprove ||
                        !userCanApprove ||
                        isMissingDocs ||
                        isVendorPaid ||
                        isEditSaving)),
                    React.createElement("div", { "data-testid": "div-2df24545", className: "px-1" }, this.renderDeclineButton(type, isShipmentCanceled(shipment) ||
                        isProcessed ||
                        disablePayeeDecline ||
                        isVendorPaid ||
                        isEditSaving)))));
        }
        if (isMissingDocs) {
            return (React.createElement("div", { "data-testid": "div-7704ff15", className: "flex justify-end -mx-1" },
                React.createElement("div", { "data-testid": "div-4381acd1", className: "px-1" }, this.renderEditButton(type, isShipmentCanceled(shipment) ||
                    isProcessed ||
                    disablePayeeEdit ||
                    !this.state.accessorialFetched ||
                    !isEditable ||
                    isVendorPaid ||
                    isAnySaving)),
                React.createElement(OverlayTrigger, { placement: "top", overlay: TOOLTIPS.MISSING_DOCS },
                    React.createElement("div", { "data-testid": "div-1ee7bce9", className: "px-1" }, this.renderAcceptButton(type, isShipmentCanceled(shipment) ||
                        isProcessed ||
                        disablePayeeApprove ||
                        !accessorialApprovalAllowed() ||
                        isAnySaving))),
                React.createElement("div", { "data-testid": "div-4ac0419f", className: "px-1" }, this.renderDeclineButton(type, isShipmentCanceled(shipment) ||
                    isProcessed ||
                    disablePayeeDecline ||
                    isVendorPaid ||
                    isAnySaving))));
        }
        return (React.createElement("div", { "data-testid": "div-e7459dea", className: "flex justify-end -mx-1" },
            React.createElement("div", { "data-testid": "div-ede77ccd", className: "px-1" }, this.renderEditButton(type, isShipmentCanceled(shipment) ||
                isProcessed ||
                disablePayeeEdit ||
                !this.state.accessorialFetched ||
                !isEditable ||
                isVendorPaid ||
                isAnySaving)),
            React.createElement("div", { "data-testid": "div-9afb95e9", className: "px-1" }, this.renderAcceptButton(type, isShipmentCanceled(shipment) ||
                isProcessed ||
                disablePayeeApprove ||
                !this.state.accessorialFetched ||
                !userCanApprove ||
                isMissingDocs ||
                isVendorPaid ||
                isAnySaving)),
            React.createElement("div", { "data-testid": "div-6bea4cb9", className: "px-1" }, this.renderDeclineButton(type, isShipmentCanceled(shipment) ||
                isProcessed ||
                disablePayeeDecline ||
                isVendorPaid ||
                isAnySaving))));
    }
    renderEditButton(type, disabled = false) {
        return (React.createElement(EditButton, { dataTestId: "EditButton-ed69f1e2", disabled: disabled, onClick: () => this.handleEditToggle(type) }));
    }
    renderCarrierMismatchBanner() {
        var _a, _b;
        const { shipment, t } = this.props;
        const { accessorial } = this.state;
        // Check if there's a carrier mismatch
        const shipmentCarrier = ((_a = shipment === null || shipment === void 0 ? void 0 : shipment.carrier) === null || _a === void 0 ? void 0 : _a._id) || (shipment === null || shipment === void 0 ? void 0 : shipment.carrier);
        const carrierAssociatedWithAccessorial = ((_b = accessorial.carrier) === null || _b === void 0 ? void 0 : _b._id) || accessorial.carrier;
        const carrierMismatch = accessorial.payee_type === 'carrier' &&
            shipmentCarrier !== carrierAssociatedWithAccessorial;
        // Check if payable is already processed
        const payableIsProcessed = accessorialPaid(shipment, accessorial);
        // Check if accessorial is already declined
        const alreadyDeclined = accessorial.carrier_status === 'declined';
        // Only show banner if there's a mismatch, payable is not processed, and not already declined
        if (!carrierMismatch || payableIsProcessed || alreadyDeclined) {
            return null;
        }
        return (React.createElement(Alert, { dataTestId: "Alert-Carrier-Mismatch-Info", type: "info", className: "mb-4" }, t('CARRIER_MISMATCH_DECLINE_INFO', {
            defaultValue: 'The carrier on this accessorial does not match the shipment carrier. You can still manually decline this accessorial if needed.',
        })));
    }
    renderDropdown(disabled) {
        var _a, _b;
        const { t, shipment } = this.props;
        const style = {
            backgroundColor: 'white',
            color: '#25AA82',
            borderColor: '#25AA82',
            fontSize: '.875rem',
            fontWeight: '700',
            borderRadius: '.25rem',
        };
        const { isAccessorialsAdmin, accessorial, isEditSaving, isApproveSaving, isDeclineSaving, } = this.state;
        const isAnySaving = isEditSaving || isApproveSaving || isDeclineSaving;
        if (disabled) {
            style.pointerEvents = 'none';
        }
        // Disable the entire dropdown when any save operation is in progress
        const effectiveDisabled = disabled || isAnySaving;
        const dropdownClass = classnames({
            button: true,
            'mr-2': true,
            'button-green': !effectiveDisabled,
        });
        const isMissingDocs = !!(accessorial === null || accessorial === void 0 ? void 0 : accessorial.missing_docs) ||
            (accessorial === null || accessorial === void 0 ? void 0 : accessorial.customer_approval_provided) === false;
        const isMissingEquipment = checkIfEquipmentIsMissingForAccessorial(accessorial);
        const shipperSideDeclined = checkWhichSideAccessorialAutoDeclined(accessorial, 'shipper');
        const carrierSideDeclined = checkWhichSideAccessorialAutoDeclined(accessorial, 'carrier');
        const isCarrierMissing = isCarrierMissingInAccessorial(accessorial);
        const accessorialDeclined = shipperSideDeclined || carrierSideDeclined;
        // Check for carrier mismatch
        const shipmentCarrier = ((_a = shipment === null || shipment === void 0 ? void 0 : shipment.carrier) === null || _a === void 0 ? void 0 : _a._id) || (shipment === null || shipment === void 0 ? void 0 : shipment.carrier);
        const carrierAssociatedWithAccessorial = ((_b = accessorial.carrier) === null || _b === void 0 ? void 0 : _b._id) || accessorial.carrier;
        const carrierMismatch = accessorial.payee_type === 'carrier' &&
            shipmentCarrier !== carrierAssociatedWithAccessorial;
        const payableIsProcessed = accessorialPaid(shipment, accessorial);
        const canUserApprove = () => {
            if (isMissingEquipment || accessorialDeclined || isCarrierMissing) {
                return false;
            }
            const canSelect = this.userCanApproveAccessorial(accessorial);
            if (canSelect && isAccessorialsAdmin) {
                return true;
            }
            return canSelect && !isMissingDocs;
        };
        // Approve button overlay
        let approveTooltipOverlay = TOOLTIPS.MISSING_DOCS;
        if (isMissingEquipment)
            approveTooltipOverlay = TOOLTIPS.MISSING_EQUIPMENT;
        if (isCarrierMissing)
            approveTooltipOverlay = TOOLTIPS.NO_CARRIER_ASSIGNED_INFO;
        if (accessorialDeclined)
            approveTooltipOverlay = TOOLTIPS.SHIPPER_CHANGED;
        // Decline button overlay - also check if carrier mismatch with processed payable
        const disableDeclineBtn = accessorialDeclined ||
            isCarrierMissing ||
            (carrierMismatch && payableIsProcessed);
        const declineTooltipOverlay = accessorialDeclined
            ? TOOLTIPS.SHIPPER_CHANGED
            : TOOLTIPS.NO_CARRIER_ASSIGNED_INFO;
        return (React.createElement(DropdownButton, { disabled: effectiveDisabled, title: t('ACTIONS'), onSelect: this.handleBulkActionMenu, bsSize: "small", id: "acessorial-bulk-actions", style: style, className: dropdownClass },
            !canUserApprove() ? (React.createElement(OverlayTrigger, { placement: "left", overlay: approveTooltipOverlay },
                React.createElement(MenuItem, { eventKey: "approve-all", disabled: !canUserApprove() }, t('APPROVE_ALL')))) : (React.createElement(MenuItem, { eventKey: "approve-all", disabled: !canUserApprove() }, t('APPROVE_ALL'))),
            disableDeclineBtn ? (React.createElement(OverlayTrigger, { placement: "left", overlay: declineTooltipOverlay },
                React.createElement(MenuItem, { eventKey: "decline-all", disabled: disableDeclineBtn }, t('DECLINE_ALL')))) : (React.createElement(MenuItem, { eventKey: "decline-all" }, t('DECLINE_ALL')))));
    }
    renderBulkActionMenu() {
        var _a, _b;
        const { shipment } = this.props;
        const { accessorial, isEditSaving, isApproveSaving, isDeclineSaving } = this.state;
        const isAnySaving = isEditSaving || isApproveSaving || isDeclineSaving;
        const isProcessed = accessorialProcessed(shipment, accessorial);
        const shipmentCarrier = ((_a = shipment === null || shipment === void 0 ? void 0 : shipment.carrier) === null || _a === void 0 ? void 0 : _a._id) || (shipment === null || shipment === void 0 ? void 0 : shipment.carrier);
        const carrierAssociatedWithAccessorial = ((_b = accessorial.carrier) === null || _b === void 0 ? void 0 : _b._id) || accessorial.carrier;
        const carrierMismatch = accessorial.payee_type === 'carrier' &&
            shipmentCarrier !== carrierAssociatedWithAccessorial;
        // Check if already approved or declined
        const isAlreadyApproved = accessorial.carrier_status === 'approved' &&
            accessorial.shipper_status === 'approved';
        // For bulk actions, approve is disabled on mismatch OR if already fully approved, decline only if processed OR already declined
        const disableBulkApprove = carrierMismatch || isAlreadyApproved;
        // Use the more restrictive for the whole dropdown (approve restriction)
        const disablePayeeResolutions = disableBulkApprove;
        if (isProcessed) {
            return (React.createElement(OverlayTrigger, { placement: "bottom", overlay: TOOLTIPS.PROCESSED },
                React.createElement("span", { "data-testid": "span-1c69101c" }, this.renderDropdown(isShipmentCanceled(shipment) ||
                    isProcessed ||
                    disableBulkApprove ||
                    isAnySaving))));
        }
        return this.renderDropdown(isShipmentCanceled(shipment) ||
            isProcessed ||
            disablePayeeResolutions ||
            isAnySaving);
    }
    getTotal(rate, qty, currency) {
        if (rate !== 0 && (!rate || !qty || !parseFloat(rate))) {
            return 'N/A';
        }
        const total = formatMoney(parseFloat(rate * qty), currency);
        if (String(total).indexOf('.') < 0) {
            return String(total) + '.00';
        }
        return String(total);
    }
    render() {
        var _a, _b, _c, _d, _e;
        const { t, dataTestId } = this.props;
        const { accessorial, shipper_unit_qty, carrier_unit_qty, carrier_rate, shipper_rate, carrier_status, shipper_status, vendor, message, history, } = this.state;
        const shipperStatusColor = colorLabelMap[shipper_status];
        const carrierStatusColor = colorLabelMap[carrier_status];
        let sortedHistory = [];
        if (history === null || history === void 0 ? void 0 : history.length) {
            sortedHistory = _.sortBy(history, 'timestamp').reverse();
        }
        let payableLabel;
        if (accessorial.payee_type === USER_TYPES.PARTNER) {
            payableLabel = t('PARTNER_REQUESTED_RATE');
        }
        else if (vendor) {
            payableLabel = t('VENDOR_REQUESTED_RATE');
        }
        else {
            payableLabel = t('CARRIER_REQUESTED_RATE');
        }
        const approveMessage = (accessorial === null || accessorial === void 0 ? void 0 : accessorial.missing_docs) === true
            ? missingDocsApproveMessage
            : defaultApproveMessage;
        const disablePrimaryButton = () => {
            const isNotFilledCorrectly = this.state.confirmType === USER_TYPES.CARRIER
                ? (!this.state.declineReasonCode &&
                    this.state.accessorial.payee_type !== USER_TYPES.VENDOR) ||
                    !this.state.declineNote.trim()
                : !this.state.declineNote.trim();
            return this.state.confirmDecline ? isNotFilledCorrectly : false;
        };
        const { auto_applied = false, needs_review = false } = accessorial;
        return (React.createElement("div", { "data-testid": dataTestId, className: "p-6" },
            React.createElement("div", { "data-testid": "div-f1c640a9", className: "flex items-center justify-between" },
                React.createElement("h1", { "data-testid": "h1-685d7482", className: "inline-flex my-0 text-2xl text-grey-darker" },
                    message,
                    " (",
                    this.props.shipment.shipment_id,
                    ")"),
                this.renderBulkActionMenu()),
            React.createElement("hr", { "data-testid": "hr-6d5505b5", className: "mt-2 mb-4" }),
            auto_applied && (React.createElement(Alert, { dataTestId: "Alert-Auto-Apply-Needs-Review-Side-Pane", type: "info" }, t(AUTO_APPLY_NEEDS_REVIEW_INFO_TEXT_MAPPING[needs_review]))),
            React.createElement("div", { "data-testid": "div-6012ab9e", className: "flex justify-between my-5" },
                React.createElement(StatHeading, { dataTestId: "StatHeading-fb7f618b", label: accessorial.payer_type === USER_TYPES.CONSIGNEE
                        ? t('CONSIGNEE_SUGGESTED_RATE')
                        : t('SHIPPER_SUGGESTED_RATE'), color: "green", value: this.getTotal(shipper_rate, shipper_unit_qty, accessorial === null || accessorial === void 0 ? void 0 : accessorial.currency) }),
                React.createElement(Badge, { dataTestId: "Badge-0375605f", text: humanize(titleize(shipper_status)), color: shipperStatusColor })),
            this.renderShipperRequestRow(),
            React.createElement("hr", { "data-testid": "hr-b1e5e541" }),
            React.createElement("div", { "data-testid": "div-cd8d1d4f", className: "flex justify-between mb-5" },
                React.createElement(StatHeading, { dataTestId: "StatHeading-825dfaa3", label: payableLabel, color: "green", value: this.getTotal(carrier_rate, carrier_unit_qty, accessorial === null || accessorial === void 0 ? void 0 : accessorial.currency) }),
                React.createElement(Badge, { dataTestId: "Badge-3d549416", text: humanize(titleize(carrier_status)), color: carrierStatusColor })),
            this.renderCarrierMismatchBanner(),
            this.renderCarrierRequestRow(),
            React.createElement("hr", { "data-testid": "hr-0a8894f9" }),
            React.createElement("div", { "data-testid": "div-b6546493" },
                React.createElement("label", { "data-testid": "label-b33c90f8", className: "form-label" },
                    t('EQUIPMENT_CONTAINERS'),
                    ":")),
            React.createElement("div", { "data-testid": "div-c1b703fb", className: "mt-2" }, this.renderEquipment()),
            React.createElement("hr", { "data-testid": "hr-682757bf" }),
            React.createElement("div", { "data-testid": "div-80d8e84e", className: "flex flex-col justify-around mt-2" }, accessorial && (React.createElement(ViewAccessorialRequestDocumentRequirements, { shipment: this.props.shipment, accessorial: Object.assign(Object.assign({}, accessorial), { slug: accessorial === null || accessorial === void 0 ? void 0 : accessorial.accessorial_type }), foreignKeys: [accessorial === null || accessorial === void 0 ? void 0 : accessorial._id, (_b = (_a = this.props) === null || _a === void 0 ? void 0 : _a.shipment) === null || _b === void 0 ? void 0 : _b._id], shipper: ((_d = (_c = this.props.shipment) === null || _c === void 0 ? void 0 : _c.shipper) === null || _d === void 0 ? void 0 : _d._id) ||
                    ((_e = this.props.shipment) === null || _e === void 0 ? void 0 : _e.shipper), isExistingAccessorialRequest: true }))),
            React.createElement("hr", { "data-testid": "hr-4c2f332e" }),
            React.createElement("div", { "data-testid": "div-4ff31917", className: "flex justify-between" },
                React.createElement(AuditComments, { comments: sortedHistory })),
            this.state.showConfirmDeclineModal && (React.createElement(ConfirmModal, { dataTestId: "ConfirmModal-03f9282c", show: this.state.showConfirmDeclineModal, payableType: this.state.accessorial.payableType, title: this.state.confirmDecline ? t(declineTitle) : t(approveTitle), message: this.state.confirmDecline ? (React.createElement(DeclineAllComponent, { error: this.state.errors.declineNote, value: this.state.declineNote, declineReasonCode: this.state.declineReasonCode, onNoteChange: this.handleDeclineNote, onDeclineReasonCodeChange: this.state.confirmType === USER_TYPES.CARRIER &&
                        this.state.accessorial.payee_type === USER_TYPES.CARRIER &&
                        this.handleDeclineReasonCode, confirmType: this.state.confirmType })) : (t(approveMessage)), onHide: () => this.setState({
                    showConfirmDeclineModal: false,
                    declineReasonCode: null,
                }), primaryDisabled: disablePrimaryButton(), onConfirm: this.confirmAction })),
            this.state.showConfirmDeclineBulkModal && (React.createElement(ConfirmModal, { dataTestId: "ConfirmModal-67dbe1b4", show: this.state.showConfirmDeclineBulkModal, payableType: this.state.accessorial.payableType, title: this.state.event && this.state.event === 'decline-all'
                    ? t(declineTitle)
                    : t(approveTitle), message: this.state.event && this.state.event === 'decline-all' ? (React.createElement(DeclineAllComponent, { error: this.state.errors.declineNote, value: this.state.declineNote, declineReasonCode: this.state.declineReasonCode, onNoteChange: this.handleDeclineNote, onDeclineReasonCodeChange: this.state.accessorial.payee_type === USER_TYPES.CARRIER &&
                        this.handleDeclineReasonCode, confirmType: this.state.confirmType })) : (t(approveAllMessage)), onHide: () => this.setState({
                    showConfirmDeclineBulkModal: false,
                    declineReasonCode: null,
                }), primaryDisabled: this.state.event &&
                    this.state.event === 'decline-all' &&
                    this.state.accessorial.payee_type === USER_TYPES.CARRIER &&
                    !this.state.declineReasonCode, onConfirm: this.handleConfirmBulk }))));
    }
}
AccessorialRequestPane.propTypes = {
    shipment: PropTypes.object.isRequired,
    accessorial: PropTypes.string.isRequired,
    showAccessorialRequestPane: PropTypes.bool,
};
AccessorialRequestPane.defaultProps = {
    showAccessorialRequestPane: false,
};
const DeclineAllComponent = (props) => {
    const { t } = useTranslation();
    const { value, onNoteChange, error, declineReasonCode, onDeclineReasonCodeChange, confirmType, } = props;
    const [accessorialsDeclineReasonCodes, setAccessorialsDeclineReasonCodes] = useState([]);
    useEffect(() => {
        async function fetchReasonCodes() {
            const accessorialsReasonCodes = await getDeclinedAccessorialsReasonCodes();
            setAccessorialsDeclineReasonCodes(accessorialsReasonCodes);
        }
        if (onDeclineReasonCodeChange) {
            try {
                fetchReasonCodes();
            }
            catch (error) {
                console.log('Unable to get decline reason codes', error);
            }
        }
    }, []);
    return (React.createElement("div", { "data-testid": "div-4628a3c7" },
        React.createElement("span", { "data-testid": "span-d0cc0229" }, t('PROVIDE_DECLINE_NOTE_FOR_THIS_REQUEST')),
        React.createElement("br", { "data-testid": "br-92f7f36f" }),
        confirmType === USER_TYPES.CARRIER && (React.createElement("span", { "data-testid": "span-afaac33c", className: "text-xs text-red-700" }, t('CARRIER_RECEIVE_NOTE_VIA_EMAIL'))),
        React.createElement("div", { "data-testid": "div-43d27076", className: "py-2" },
            React.createElement(CargomaticInput, { dataTestId: "CargomaticInput-415650de", error: error, value: value, label: titleize(t('NOTE', { count: 3 })), info: t('PROVIDE_DECLINE_NOTE'), placeholder: titleize(t('NOTE', { count: 3 })), onChange: (note) => onNoteChange(note), maxLength: 100 })),
        onDeclineReasonCodeChange && (React.createElement("div", { "data-testid": "div-ca400cc4" },
            React.createElement("div", { "data-testid": "div-ef6ba087", className: "py-2" }, t('SELECT_REASON_CODE')),
            React.createElement(CargomaticSelect, { dataTestId: "CargomaticSelect-94c18db3", items: accessorialsDeclineReasonCodes, value: declineReasonCode, className: "w-auto", hideLabel: true, showCaption: true, onSelect: onDeclineReasonCodeChange })))));
};
export default withTranslation()(AccessorialRequestPane);
